// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT
import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import notification from 'antd/lib/notification';
import { ActionUnion, createAction, ThunkAction } from '../utils/redux';
import { CombinedState, GradesState } from '../reducers/interfaces';
import {
    computeTotal, calculateOverall, getGradeNickname, mapGradeValue,
} from '../utils/grades';

const certificateNotFound = (message: string): Error => new Error(`${message}, certificate number not found!`);
const orientationNotFound = new Error('Cannot reload RoboGrades, orientation not found!');

export enum GradesActionsTypes {
    LOAD_VALUES = 'grades@LOAD_VALUES',
    ASSIGN_VALUES = 'grades@ASSIGN_VALUES',
    UPDATE_VALUE = 'grades@UPDATE_VALUE',
    SET_GRADES_LOADING = 'grades@SET_GRADES_LOADING',
    SET_GENERATE_ROBOGRADES_LOADING = 'grades@SET_GENERATE_ROBOGRADES_LOADING',
    SET_UPDATE_HUMAN_GRADES_LOADING = 'grades@SET_UPDATE_HUMAN_GRADES_LOADING',
    SET_ERROR = 'grades@SET_ERROR',
    SET_WARNING = 'grades@SET_WARNING',
}

export const gradesActions = {
    setGrades: (values: GradesState['values']) => createAction(GradesActionsTypes.LOAD_VALUES, { values }),
    assignGrades: (values: Partial<GradesState['values']>) => (
        createAction(GradesActionsTypes.ASSIGN_VALUES, { values })
    ),
    setGradesLoading: (grades_loading: boolean) => (
        createAction(GradesActionsTypes.SET_GRADES_LOADING, { grades_loading })
    ),
    setGenerateRobogradesLoading: (generate_robogrades_loading: boolean) => (
        createAction(GradesActionsTypes.SET_GENERATE_ROBOGRADES_LOADING, { generate_robogrades_loading })
    ),
    setUpdateHumanGradesLoading: (update_human_grades_loading: boolean) => (
        createAction(GradesActionsTypes.SET_UPDATE_HUMAN_GRADES_LOADING, { update_human_grades_loading })
    ),
    setError: (error: string | Error | null) => createAction(GradesActionsTypes.SET_ERROR, { error }),
    setWarning: (warning: string | Error | null) => createAction(GradesActionsTypes.SET_WARNING, { warning }),
    updateValue: (key: keyof GradesState['values'] | string, value: string | number) => (
        createAction(GradesActionsTypes.UPDATE_VALUE, { key, value })
    ),
};

export type GradesActions = ActionUnion<typeof gradesActions>;

const AGS_API_POLL_INTERVAL_MS = 2000;

// The AGS API is called through the CVAT server (cvat/apps/engine/ags_api.py), which holds the AGS
// API token: the bundle is public. An answer that takes the AGS API long comes back as an id to poll.
async function apiCall(endpoint: string, opts: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    let response = await Axios.request({ ...opts, url: `/api/v1/ags-api${endpoint}` });
    while (response.status === 202 && response.data?.ags_request_id) {
        await new Promise((resolve) => setTimeout(resolve, AGS_API_POLL_INTERVAL_MS));
        response = await Axios.get(`/api/v1/ags-api/requests/${response.data.ags_request_id}`);
    }
    return response;
}

async function sendGrades(action: string, values: any): Promise<void> {
    try {
        await Axios.post('/api/v1/cvat-grades', { action, values });
    } catch (e) {
        console.error('error sending grades', e);
    }
}

export const setErrorAsync =
    (error: string | Error): ThunkAction => async (dispatch) => {
        const axiosErr = error as AxiosError;
        if (axiosErr?.isAxiosError) {
            const { data } = axiosErr.response || {};
            dispatch(gradesActions.setError(data.detail));
        } else {
            dispatch(gradesActions.setError(error));
        }
        setTimeout(() => {
            dispatch(gradesActions.setError(null));
        }, 3000);
    };

export const setWarningAsync =
    (warning: string | Error): ThunkAction => async (dispatch) => {
        dispatch(gradesActions.setWarning(warning));
        setTimeout(() => {
            dispatch(gradesActions.setWarning(null));
        }, 3000);
    };

export const updateTaskMeta =
    (task: any, data: Record<'certificateId' | 'orderId', string>): ThunkAction => async () => {
        const { certificateId, orderId } = data;
        const task$ = task;

        if (orderId) {
            task$.orderId = orderId;
        }

        if (certificateId) {
            task$.certificateId = certificateId;
        }

        await task.save();
    };

export const loadingGradesAsync =
    (certificateId?: string | number): ThunkAction => async (dispatch) => {
        if (!certificateId) {
            dispatch(setErrorAsync(certificateNotFound('Cannot load grades')));
            return;
        }

        try {
            dispatch(gradesActions.setGradesLoading(true));

            const { data } = await apiCall(`/v2/robograding/scan-results/?certificate_ids=${certificateId}`);
            const result = (data.results || [])[0];

            dispatch(
                gradesActions.setGrades({
                    back_centering_human_grade: mapGradeValue(result?.back_centering_human_grade),
                    back_corners_human_grade: mapGradeValue(result?.back_corners_human_grade),
                    back_edges_human_grade: mapGradeValue(result?.back_edges_human_grade),
                    back_surface_human_grade: mapGradeValue(result?.back_surface_human_grade),
                    front_centering_human_grade: mapGradeValue(result?.front_centering_human_grade),
                    front_corners_human_grade: mapGradeValue(result?.front_corners_human_grade),
                    front_edges_human_grade: mapGradeValue(result?.front_edges_human_grade),
                    front_surface_human_grade: mapGradeValue(result?.front_surface_human_grade),
                    back_centering_laser_grade: mapGradeValue(
                        result?.laser_back_scan?.centering_result?.results?.grade,
                    ),
                    back_corners_laser_grade: mapGradeValue(result?.laser_back_scan?.corners_result?.results?.grade),
                    back_edges_laser_grade: mapGradeValue(result?.laser_back_scan?.edges_result?.results?.grade),
                    back_surface_laser_grade: mapGradeValue(result?.laser_back_scan?.surface_result?.results?.grade),
                    front_centering_laser_grade: mapGradeValue(
                        result?.laser_front_scan?.centering_result?.results?.grade,
                    ),
                    front_corners_laser_grade: mapGradeValue(result?.laser_front_scan?.corners_result?.results?.grade),
                    front_edges_laser_grade: mapGradeValue(result?.laser_front_scan?.edges_result?.results?.grade),
                    front_surface_laser_grade: mapGradeValue(result?.laser_front_scan?.surface_result?.results?.grade),
                    front_overall_predicted_grade: mapGradeValue(result?.laser_front_scan?.overall_predicted_grade),
                    back_overall_predicted_grade: mapGradeValue(result?.laser_back_scan?.overall_predicted_grade),
                    front_overall_from_subgrades: mapGradeValue(result?.laser_front_scan?.overall_from_subgrades),
                    back_overall_from_subgrades: mapGradeValue(result?.laser_back_scan?.overall_from_subgrades),
                }),
            );
        } catch (error) {
            if (error.isAxiosError) {
                const { data } = error.response;
                dispatch(setErrorAsync(data.detail));
            } else {
                dispatch(setErrorAsync(error));
            }
        }

        dispatch(gradesActions.setGradesLoading(false));
    };

export type GenerateMode = 'grades' | 'masks' | 'grades_and_masks' | 'grades_and_masks_and_human_grades';

interface SubmitAnnotationFrameInput {
    image?: File | Blob;
    orientation?: 'front' | 'back' | string | null;
    imageType?: 'laser' | 'cam' | string | null;
    certificateId?: string | null;
    generateMode?: GenerateMode;
}

export const submitAnnotationFrameToGradeAsync =
    (
        input: SubmitAnnotationFrameInput & {
            noNotifications?: boolean;
            frame?: any;
            annotation?: any;
            job?: any;
            resolve?: () => void;
            reject?: (e: any) => void;
        },
    ): ThunkAction => async (dispatch, getState) => {
        dispatch(gradesActions.setGenerateRobogradesLoading(true));

        if (!input.orientation) {
            dispatch(setWarningAsync(orientationNotFound));
        }

        const { resolve, reject } = input;
        const state = getState() as CombinedState;
        const states: any[] = input.annotation || state.annotation?.annotations?.states || [];
        const { frame: annotationFrame } = state.annotation.player;
        const formData = new FormData();

        const theFrame = input.frame || annotationFrame;
        const frameName = theFrame.filename;

        const job = input.job || state.annotation.job.instance;
        const image = await job.frames.frameData((theFrame || annotationFrame).number);
        formData.append('image', image, frameName);

        formData.append(
            'payload',
            JSON.stringify({
                filename: frameName,
                objects: states.map((item) => ({
                    points: item.points,
                    label: item.label.name,
                    shape: item.shapeType,
                })),
                image: {
                    width: theFrame.width ?? theFrame.data.width,
                    height: theFrame.height ?? theFrame.data.height,
                },
            }),
        );

        if (input.orientation) {
            formData.append('orientation', input.orientation);
        }
        if (input.imageType) {
            formData.append('image_type', input.imageType);
        }
        if (input.certificateId) {
            formData.append('certificate_id', input.certificateId);
        }

        const generateMode = input.generateMode || 'grades_and_masks';
        formData.append('generate_mode', generateMode);

        const modeLabels: Record<GenerateMode, string> = {
            grades: 'RoboGrades',
            masks: 'Masks',
            grades_and_masks: 'RoboGrades & Masks',
            grades_and_masks_and_human_grades: 'RoboGrades & Masks + Human Grades',
        };
        const modeLabel = modeLabels[generateMode];

        try {
            if (!input.noNotifications) {
                notification.info({
                    message: `Generating ${modeLabel}...`,
                });
            }
            const { data } = await apiCall('/cvat-to-grade/', {
                method: 'POST',
                data: formData,
            });

            if (input.orientation && generateMode !== 'masks') {
                // Update laser grades (robogrades) from the API response
                const grades: Record<string, any> = {
                    [`${input.orientation}_centering_laser_grade`]: mapGradeValue(data?.center),
                    [`${input.orientation}_corners_laser_grade`]: mapGradeValue(data?.corner),
                    [`${input.orientation}_edges_laser_grade`]: mapGradeValue(data?.edge),
                    [`${input.orientation}_surface_laser_grade`]: mapGradeValue(data?.surface),
                    [`${input.orientation}_overall_predicted_grade`]: mapGradeValue(data?.overall),
                    [`${input.orientation}_overall_from_subgrades`]: mapGradeValue(data?.overall_from_subgrades),
                };

                // Also assign human grades when the mode includes human grades
                if (generateMode === 'grades_and_masks_and_human_grades') {
                    grades[`${input.orientation}_centering_human_grade`] = mapGradeValue(data?.center);
                    grades[`${input.orientation}_corners_human_grade`] = mapGradeValue(data?.corner);
                    grades[`${input.orientation}_edges_human_grade`] = mapGradeValue(data?.edge);
                    grades[`${input.orientation}_surface_human_grade`] = mapGradeValue(data?.surface);
                }

                dispatch(gradesActions.assignGrades(grades));
            }

            if (!input.noNotifications) {
                notification.success({
                    message: `${modeLabel} generated successfully.`,
                });
            }
            if (resolve) {
                resolve();
            }
        } catch (e) {
            if (!input.noNotifications) {
                notification.error({
                    message: `${modeLabel} couldn't be generated.`,
                });
            }
            dispatch(setErrorAsync(e));
            if (reject) {
                reject(e);
            }
        }
        dispatch(gradesActions.setGenerateRobogradesLoading(false));
    };

export const submitHumanGradesAsync =
    (certificateId?: string | number | null): ThunkAction => async (dispatch, getState) => {
        if (!certificateId) {
            dispatch(setErrorAsync(certificateNotFound('Cannot submit human grades')));
            return;
        }

        const state = getState() as CombinedState;
        const { values } = state.grades;

        const overallCenteringGrade = calculateOverall(
            values.front_centering_human_grade,
            values.back_centering_human_grade,
        );
        const overallCornersGrade = calculateOverall(values.front_corners_human_grade, values.back_corners_human_grade);
        const overallEdgesGrade = calculateOverall(values.front_edges_human_grade, values.back_edges_human_grade);
        const overallSurfaceGrade = calculateOverall(values.front_surface_human_grade, values.back_surface_human_grade);

        const overallGrade = computeTotal([
            overallCenteringGrade,
            overallCornersGrade,
            overallEdgesGrade,
            overallSurfaceGrade,
        ]);

        const overallGradeNickname = getGradeNickname(overallGrade);
        try {
            dispatch(gradesActions.setUpdateHumanGradesLoading(true));
            notification.info({
                message: 'Updating human grades...',
            });

            const data = {
                front_centering_human_grade: mapGradeValue(values.front_centering_human_grade),
                front_corners_human_grade: mapGradeValue(values.front_corners_human_grade),
                front_edges_human_grade: mapGradeValue(values.front_edges_human_grade),
                front_surface_human_grade: mapGradeValue(values.front_surface_human_grade),
                back_centering_human_grade: mapGradeValue(values.back_centering_human_grade),
                back_corners_human_grade: mapGradeValue(values.back_corners_human_grade),
                back_edges_human_grade: mapGradeValue(values.back_edges_human_grade),
                back_surface_human_grade: mapGradeValue(values.back_surface_human_grade),
                overall_centering_grade: overallCenteringGrade,
                overall_corners_grade: overallCornersGrade,
                overall_edges_grade: overallEdgesGrade,
                overall_surface_grade: overallSurfaceGrade,
                overall_grade: {
                    grade: overallGrade,
                    nickname: overallGradeNickname,
                },
            };

            const [{ data: res }] = await Promise.all([
                apiCall(`/v2/robograding/certificates/?certificate_id=${certificateId}`, {
                    method: 'PATCH',
                    data,
                }),
                sendGrades('human_grades', {
                    certificateId,
                    grades: data,
                }),
            ]);

            dispatch(
                gradesActions.assignGrades({
                    back_centering_human_grade: mapGradeValue(res.back_centering_human_grade),
                    back_corners_human_grade: mapGradeValue(res.back_corners_human_grade),
                    back_edges_human_grade: mapGradeValue(res.back_edges_human_grade),
                    back_surface_human_grade: mapGradeValue(res.back_surface_human_grade),
                    front_centering_human_grade: mapGradeValue(res.front_centering_human_grade),
                    front_corners_human_grade: mapGradeValue(res.front_corners_human_grade),
                    front_edges_human_grade: mapGradeValue(res.front_edges_human_grade),
                    front_surface_human_grade: mapGradeValue(res.front_surface_human_grade),
                }),
            );

            notification.success({
                message: 'Human grades has been updated successfully.',
            });
        } catch (e) {
            notification.error({
                message: 'Human grades couldn\'t be updated.',
            });
            dispatch(setErrorAsync(e));
        }

        dispatch(gradesActions.setUpdateHumanGradesLoading(false));
    };
